import * as THREE from "three";
import type { QualityProfile } from "../../app/performance";
import { RealisticHuntAssets, type HareInstance } from "./RealisticHuntAssets";

export type HuntTargetType = "hare";

export type HuntShotResult = {
  hit: boolean;
  score: number;
  label: "CRITICAL" | "HIT" | "MISS";
  targetType?: HuntTargetType;
};

type HareTarget = {
  id: number;
  root: THREE.Group;
  mixer: THREE.AnimationMixer | null;
  action: THREE.AnimationAction | null;
  baseX: number;
  phase: number;
  lateralAmplitude: number;
  fleeSpeed: number;
  radius: number;
  hit: boolean;
  hitLife: number;
};

type ActiveArrow = {
  root: THREE.Group;
  velocity: THREE.Vector3;
  previous: THREE.Vector3;
  age: number;
  resolved: boolean;
};

type ForestProp = {
  root: THREE.Group;
  recycleDistance: number;
  lane: "left" | "right" | "ground";
};

export class CreatureHuntArena {
  readonly group = new THREE.Group();

  private readonly assets = new RealisticHuntAssets();
  private readonly hares = new Map<number, HareTarget>();
  private readonly arrows = new Set<ActiveArrow>();
  private readonly forest = new THREE.Group();
  private readonly bow = new THREE.Group();
  private readonly nockedArrow = new THREE.Group();
  private readonly forestProps: ForestProp[] = [];

  private bowString: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> | null = null;
  private preparePromise: Promise<void> | null = null;
  private assetsReady = false;
  private assetsFailed = false;
  private active = false;
  private aimX = 0;
  private aimY = 0;
  private drawPower = 0;
  private scoped = false;
  private nextHareId = 1;
  private pendingHares = 0;
  private reloadLife = 0;
  private runDistance = 0;

  constructor(
    private readonly quality: QualityProfile,
    private readonly onShot: (result: HuntShotResult) => void,
  ) {
    this.group.visible = false;
    this.group.add(this.buildNaturalStage(), this.forest);

    this.buildBow();
    this.group.add(this.bow);
  }

  prepare(): Promise<void> {
    if (this.preparePromise) return this.preparePromise;

    this.preparePromise = this.assets
      .prepare(this.quality.tier === "quality")
      .then(async () => {
        await this.buildForestAssets();
        this.assetsReady = true;

        if (this.active) this.reset();
      })
      .catch(() => {
        // Runtime network failure must not crash the game. The natural base stage
        // and bow remain usable, while the UI can still enter a degraded preview.
        this.assetsFailed = true;
        this.assetsReady = false;
      });

    return this.preparePromise;
  }

  isProfessionalAssetReady(): boolean {
    return this.assetsReady && !this.assetsFailed;
  }

  setActive(value: boolean): void {
    this.active = value;
    this.group.visible = value;

    if (value) {
      this.reset();
    } else {
      this.setDrawPower(0);
    }
  }

  reset(): void {
    this.clearDynamic();
    this.reloadLife = 0;
    this.runDistance = 0;
    this.nockedArrow.visible = true;

    if (this.assetsReady) {
      void this.ensureHares(3);
    }
  }

  setAim(x: number, y: number): void {
    this.aimX = THREE.MathUtils.clamp(x, -1, 1);
    this.aimY = THREE.MathUtils.clamp(y, -1, 1);

    this.bow.rotation.y = -0.08 - this.aimX * 0.08;
    this.bow.rotation.x = -0.03 + this.aimY * 0.055;
  }

  setScoped(value: boolean): void {
    this.scoped = value;
  }

  setDrawPower(value: number): void {
    this.drawPower = THREE.MathUtils.clamp(value, 0, 1);

    const pull = this.drawPower * 0.24;
    this.nockedArrow.position.z = 0.03 + pull;

    if (this.bowString) {
      this.bowString.geometry.setFromPoints([
        new THREE.Vector3(0, 0.62, 0),
        new THREE.Vector3(0.075, 0, 0.035 + pull),
        new THREE.Vector3(0, -0.62, 0),
      ]);
    }
  }

  shoot(power = this.drawPower): void {
    if (!this.active || !this.nockedArrow.visible) return;

    const normalizedPower = THREE.MathUtils.clamp(power, 0.45, 1);
    const arrow = this.createArrow();

    const start = new THREE.Vector3(0.66, 1.1, 2.66);
    arrow.position.copy(start);
    this.group.add(arrow);

    const target = new THREE.Vector3(
      this.aimX * 3.4,
      1.24 + this.aimY * 2.0,
      -7.0,
    );

    const direction = target.sub(start).normalize();
    const speed = 17.8 + normalizedPower * 6.2;

    arrow.lookAt(start.clone().add(direction));

    this.arrows.add({
      root: arrow,
      velocity: direction.multiplyScalar(speed),
      previous: start.clone(),
      age: 0,
      resolved: false,
    });

    this.nockedArrow.visible = false;
    this.reloadLife = 0.18;
    this.setDrawPower(0);
  }

  update(delta: number, elapsed: number): void {
    if (!this.active) return;

    const scopeFactor = this.scoped ? 0.62 : 1;
    const runFactor = scopeFactor * (1 - this.drawPower * 0.52);
    const runSpeed = 4.25 * runFactor;
    this.runDistance += runSpeed * delta;

    this.updateForest(delta, runSpeed);
    this.updateBowRun(elapsed, runFactor);

    if (this.reloadLife > 0) {
      this.reloadLife = Math.max(0, this.reloadLife - delta);
      if (this.reloadLife === 0) this.nockedArrow.visible = true;
    }

    for (const hare of [...this.hares.values()]) {
      this.updateHare(hare, delta, elapsed, runSpeed);
    }

    if (this.assetsReady && this.hares.size + this.pendingHares < 3) {
      void this.ensureHares(3);
    }

    for (const arrow of [...this.arrows]) {
      this.updateArrow(arrow, delta);
    }
  }

  clear(): void {
    this.clearDynamic();
    this.setDrawPower(0);
    this.nockedArrow.visible = true;
  }

  private async ensureHares(targetCount: number): Promise<void> {
    while (
      this.active &&
      this.assetsReady &&
      this.hares.size + this.pendingHares < targetCount
    ) {
      this.pendingHares += 1;

      try {
        const instance = await this.assets.createHare(0.64);
        if (!this.active) {
          instance.mixer?.stopAllAction();
          return;
        }

        this.addHare(instance);
      } finally {
        this.pendingHares = Math.max(0, this.pendingHares - 1);
      }
    }
  }

  private addHare(instance: HareInstance): void {
    const id = this.nextHareId;
    this.nextHareId += 1;

    const root = instance.root;
    const lane = id % 3;
    const baseX = lane === 0 ? -1.6 : lane === 1 ? 0.25 : 1.65;

    root.position.set(
      baseX + (Math.random() - 0.5) * 0.55,
      0.02,
      -5.2 - Math.random() * 3.9,
    );

    // The source hare faces its authored forward axis. A 180-degree yaw puts it
    // visually into the same away-from-player direction as the chase lane.
    root.rotation.y = Math.PI;

    const hare: HareTarget = {
      id,
      root,
      mixer: instance.mixer,
      action: instance.action,
      baseX,
      phase: Math.random() * Math.PI * 2,
      lateralAmplitude: 0.42 + Math.random() * 0.35,
      fleeSpeed: 1.15 + Math.random() * 0.45,
      radius: 0.39,
      hit: false,
      hitLife: 0,
    };

    this.hares.set(id, hare);
    this.group.add(root);
  }

  private updateHare(
    hare: HareTarget,
    delta: number,
    elapsed: number,
    runSpeed: number,
  ): void {
    hare.mixer?.update(delta);

    if (hare.hit) {
      hare.hitLife = Math.max(0, hare.hitLife - delta);
      hare.action?.setEffectiveTimeScale(0.15);

      const settle = 1 - hare.hitLife / 0.52;
      hare.root.rotation.x = THREE.MathUtils.lerp(0, -0.85, settle);
      hare.root.position.y = Math.max(0.015, hare.root.position.y - delta * 0.18);

      if (hare.hitLife === 0) this.removeHare(hare);
      return;
    }

    const t = elapsed * 2.15 + hare.phase;
    const targetX = hare.baseX + Math.sin(t) * hare.lateralAmplitude;

    hare.root.position.x = THREE.MathUtils.damp(
      hare.root.position.x,
      targetX,
      5.4,
      delta,
    );

    // Player advances while the animal actively flees. The net motion keeps the
    // hare in a readable chase distance instead of sliding sideways in an arena.
    hare.root.position.z += (runSpeed * 0.67 - hare.fleeSpeed) * delta;
    hare.root.position.y = 0.02 + Math.max(0, Math.sin(t * 1.8)) * 0.035;
    hare.root.rotation.z = Math.sin(t) * 0.025;

    if (hare.root.position.z > -2.6 || hare.root.position.z < -11.5) {
      hare.root.position.z = -7.0 - Math.random() * 2.6;
      hare.baseX = (Math.random() - 0.5) * 3.8;
      hare.root.position.x = hare.baseX;
    }
  }

  private updateArrow(arrow: ActiveArrow, delta: number): void {
    arrow.age += delta;
    arrow.previous.copy(arrow.root.position);

    if (!arrow.resolved) {
      arrow.velocity.y -= 0.62 * delta;
      arrow.root.position.addScaledVector(arrow.velocity, delta);
      arrow.root.lookAt(arrow.root.position.clone().add(arrow.velocity));

      for (const hare of this.hares.values()) {
        if (hare.hit) continue;

        const center = hare.root.position.clone();
        center.y += 0.31;

        const distanceSq = this.segmentPointDistanceSquared(
          arrow.previous,
          arrow.root.position,
          center,
        );

        if (distanceSq <= hare.radius * hare.radius) {
          const distance = Math.sqrt(distanceSq);
          const critical = distance <= hare.radius * 0.42;

          hare.hit = true;
          hare.hitLife = 0.52;
          arrow.resolved = true;
          arrow.velocity.multiplyScalar(0.06);

          this.onShot({
            hit: true,
            score: critical ? 10 : 7,
            label: critical ? "CRITICAL" : "HIT",
            targetType: "hare",
          });
          break;
        }
      }

      if (!arrow.resolved && (arrow.root.position.z < -12.5 || arrow.age > 1.5)) {
        arrow.resolved = true;
        this.onShot({ hit: false, score: 0, label: "MISS" });
      }
    }

    if (arrow.age > (arrow.resolved ? 0.72 : 1.7) || arrow.root.position.y < -0.3) {
      this.removeArrow(arrow);
    }
  }

  private updateForest(delta: number, runSpeed: number): void {
    if (!this.assetsReady) return;

    for (const prop of this.forestProps) {
      prop.root.position.z += runSpeed * delta;

      if (prop.root.position.z > 6) {
        prop.root.position.z -= prop.recycleDistance;

        if (prop.lane === "left") {
          prop.root.position.x = -3.2 - Math.random() * 3.4;
        } else if (prop.lane === "right") {
          prop.root.position.x = 3.2 + Math.random() * 3.4;
        } else {
          prop.root.position.x = (Math.random() - 0.5) * 7.2;
        }
      }
    }
  }

  private updateBowRun(elapsed: number, runFactor: number): void {
    const sprint = Math.max(0, runFactor);
    const swayX = Math.sin(elapsed * 7.6) * 0.045 * sprint;
    const swayY = Math.abs(Math.sin(elapsed * 7.6)) * 0.035 * sprint;

    this.bow.position.x = 0.76 + swayX;
    this.bow.position.y = 0.98 - swayY + this.drawPower * 0.1;
    this.bow.rotation.z = -0.08 + Math.sin(elapsed * 7.6) * 0.025 * sprint;
  }

  private async buildForestAssets(): Promise<void> {
    if (this.forest.children.length > 0) return;

    const includeHeavyFir = this.quality.tier === "quality";

    const [
      fir,
      firDistant,
      pine,
      birch,
      grass,
      tallGrass,
      bush,
      fern,
      rock,
    ] = await Promise.all([
      includeHeavyFir ? this.assets.createProp("fir", 6.6) : Promise.resolve(null),
      this.assets.createProp("firDistant", 5.1),
      this.assets.createProp("pine", 5.0),
      this.assets.createProp("birch", 5.4),
      this.assets.createProp("grass", 0.42),
      this.assets.createProp("tallGrass", 0.62),
      this.assets.createProp("bush", 0.82),
      this.assets.createProp("fern", 0.58),
      this.assets.createProp("rock", 0.72),
    ]);

    const addProp = (
      template: THREE.Group,
      x: number,
      z: number,
      scale: number,
      lane: ForestProp["lane"],
      recycleDistance = 32,
    ): void => {
      const root = template.clone(true);
      root.position.set(x, 0.01, z);
      root.scale.multiplyScalar(scale);
      root.rotation.y = Math.random() * Math.PI * 2;
      this.forest.add(root);
      this.forestProps.push({ root, recycleDistance, lane });
    };

    const treeTemplates = includeHeavyFir && fir
      ? [fir, pine, birch, firDistant]
      : [pine, birch, firDistant];
    const foregroundTreeCount = this.quality.tier === "quality" ? 12 : 8;
    for (let i = 0; i < foregroundTreeCount; i += 1) {
      const lane: "left" | "right" = i % 2 === 0 ? "left" : "right";
      const x =
        lane === "left"
          ? -3.4 - Math.random() * 3.1
          : 3.4 + Math.random() * 3.1;
      const z = 3 - i * 2.8;
      const template = treeTemplates[i % treeTemplates.length];
      addProp(template, x, z, 0.78 + Math.random() * 0.36, lane);
    }

    const distantTreeCount = this.quality.tier === "quality" ? 8 : 6;
    for (let i = 0; i < distantTreeCount; i += 1) {
      const lane: "left" | "right" = i % 2 === 0 ? "left" : "right";
      const x = lane === "left" ? -6.4 - Math.random() * 2.4 : 6.4 + Math.random() * 2.4;
      addProp(firDistant, x, -8 - i * 3.5, 1.0 + Math.random() * 0.35, lane, 38);
    }

    const groundTemplates = [grass, tallGrass, bush, fern, rock];
    const groundPropCount = this.quality.tier === "quality" ? 22 : 14;
    for (let i = 0; i < groundPropCount; i += 1) {
      const template = groundTemplates[i % groundTemplates.length];
      const x = (Math.random() - 0.5) * 8.4;
      const z = 4 - i * 1.45;
      const scale = 0.72 + Math.random() * 0.55;
      addProp(template, x, z, scale, "ground");
    }
  }

  private buildNaturalStage(): THREE.Group {
    const stage = new THREE.Group();

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(16, 42, 12, 30),
      new THREE.MeshStandardMaterial({
        color: 0x42513a,
        roughness: 0.97,
        metalness: 0,
      }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, -0.015, -11.5);
    ground.receiveShadow = this.quality.shadows;
    stage.add(ground);

    const trail = new THREE.Mesh(
      new THREE.PlaneGeometry(3.25, 42),
      new THREE.MeshStandardMaterial({
        color: 0x665c45,
        roughness: 1,
        metalness: 0,
      }),
    );
    trail.rotation.x = -Math.PI / 2;
    trail.position.set(0, 0.006, -11.5);
    trail.receiveShadow = this.quality.shadows;
    stage.add(trail);

    const leftBank = new THREE.Mesh(
      new THREE.PlaneGeometry(4.4, 42),
      new THREE.MeshStandardMaterial({
        color: 0x354631,
        roughness: 1,
      }),
    );
    leftBank.rotation.x = -Math.PI / 2;
    leftBank.position.set(-3.8, 0, -11.5);
    stage.add(leftBank);

    const rightBank = leftBank.clone();
    rightBank.position.x = 3.8;
    stage.add(rightBank);

    return stage;
  }

  private buildBow(): void {
    const wood = new THREE.MeshStandardMaterial({
      color: 0x5f3c22,
      roughness: 0.38,
      metalness: 0.04,
    });

    const upperCurve = new THREE.CubicBezierCurve3(
      new THREE.Vector3(0.02, 0.02, 0),
      new THREE.Vector3(0.18, 0.24, 0.01),
      new THREE.Vector3(0.18, 0.48, 0.015),
      new THREE.Vector3(0, 0.62, 0),
    );
    const lowerCurve = new THREE.CubicBezierCurve3(
      new THREE.Vector3(0.02, -0.02, 0),
      new THREE.Vector3(0.18, -0.24, 0.01),
      new THREE.Vector3(0.18, -0.48, 0.015),
      new THREE.Vector3(0, -0.62, 0),
    );

    for (const curve of [upperCurve, lowerCurve]) {
      const limb = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 24, 0.026, 8, false),
        wood,
      );
      limb.castShadow = this.quality.shadows;
      this.bow.add(limb);
    }

    const grip = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.062, 0.34, 12),
      new THREE.MeshStandardMaterial({
        color: 0x2b1d16,
        roughness: 0.8,
        metalness: 0.02,
      }),
    );
    grip.rotation.z = Math.PI / 2;
    grip.position.set(0.05, 0, 0.01);
    this.bow.add(grip);

    this.bowString = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0.62, 0),
        new THREE.Vector3(0.075, 0, 0.035),
        new THREE.Vector3(0, -0.62, 0),
      ]),
      new THREE.LineBasicMaterial({
        color: 0xe8e1d4,
        transparent: true,
        opacity: 0.82,
      }),
    );
    this.bow.add(this.bowString);

    const nocked = this.createArrow();
    nocked.rotation.y = Math.PI;
    nocked.scale.setScalar(0.9);
    this.nockedArrow.add(nocked);
    this.bow.add(this.nockedArrow);

    this.bow.position.set(0.76, 0.98, 2.64);
    this.bow.scale.setScalar(1.08);
    this.setAim(0, 0);
  }

  private createArrow(): THREE.Group {
    const arrow = new THREE.Group();

    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.012, 0.012, 1.22, 10),
      new THREE.MeshStandardMaterial({
        color: 0x4b2e1b,
        roughness: 0.55,
        metalness: 0.02,
      }),
    );
    shaft.rotation.x = Math.PI / 2;
    arrow.add(shaft);

    const tip = new THREE.Mesh(
      new THREE.ConeGeometry(0.045, 0.18, 10),
      new THREE.MeshStandardMaterial({
        color: 0xaeb7c4,
        metalness: 0.86,
        roughness: 0.2,
      }),
    );
    tip.rotation.x = Math.PI / 2;
    tip.position.z = 0.69;
    arrow.add(tip);

    const featherMaterial = new THREE.MeshStandardMaterial({
      color: 0x6d3429,
      roughness: 0.86,
      side: THREE.DoubleSide,
    });

    for (const rotation of [0, Math.PI / 2]) {
      const feather = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.008, 0.2),
        featherMaterial.clone(),
      );
      feather.position.z = -0.49;
      feather.rotation.z = rotation;
      arrow.add(feather);
    }

    return arrow;
  }

  private segmentPointDistanceSquared(
    start: THREE.Vector3,
    end: THREE.Vector3,
    point: THREE.Vector3,
  ): number {
    const segment = end.clone().sub(start);
    const lengthSq = segment.lengthSq();

    if (lengthSq <= 1e-7) return point.distanceToSquared(start);

    const t = THREE.MathUtils.clamp(
      point.clone().sub(start).dot(segment) / lengthSq,
      0,
      1,
    );

    return point.distanceToSquared(start.clone().addScaledVector(segment, t));
  }

  private removeHare(hare: HareTarget): void {
    if (!this.hares.has(hare.id)) return;

    hare.mixer?.stopAllAction();
    this.hares.delete(hare.id);
    this.group.remove(hare.root);
  }

  private removeArrow(arrow: ActiveArrow): void {
    if (!this.arrows.has(arrow)) return;

    this.arrows.delete(arrow);
    this.group.remove(arrow.root);
    this.disposeArrow(arrow.root);
  }

  private clearDynamic(): void {
    for (const hare of this.hares.values()) {
      hare.mixer?.stopAllAction();
      this.group.remove(hare.root);
    }
    this.hares.clear();

    for (const arrow of this.arrows) {
      this.group.remove(arrow.root);
      this.disposeArrow(arrow.root);
    }
    this.arrows.clear();
    this.pendingHares = 0;
  }

  private disposeArrow(root: THREE.Object3D): void {
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;

      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) material.dispose();
    });
  }
}
